import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card } from "react-bootstrap";
import axios from "axios";
import { baseURL } from "../../API/API";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import Header from "../../Components/common/Header";
import Footer from "../../Components/common/Footer";
import useAuth from "../hooks/useAuth";
import { toast } from "react-toastify";
import { FaRegCalendarAlt, FaRegClock } from "react-icons/fa";
import "./GradeCourses.css";

// Helper static images from HomePage
import ImgFirst from "../../Imge/first_sec_bio.png";
import ImgSecond from "../../Imge/second_sec_bio.png";
import ImgThird from "../../Imge/third_sec_bio.png";
import FallbackImg from "../../Imge/about_2.png";
import L1Img from "../../Imge/l1.png";
import L2Img from "../../Imge/l2.png";

const getChapterDates = (name) => {
  if (!name) return { startDate: "الاثنين، 6 أبريل", startTime: "02:00 م" };
  const lowerName = name.toLowerCase();
  if (lowerName.includes("معسكر") || lowerName.includes("انقاذ") || lowerName.includes("أنقاذ")) {
    return { startDate: "الخميس، 12 يناير", startTime: "05:00 م" };
  }
  return { startDate: "الاثنين، 6 أبريل", startTime: "02:00 م" };
};

const getCourseBadge = (name) => {
  if (!name) return null;
  const lowerName = name.toLowerCase();
  if (lowerName.includes("أزهر") || lowerName.includes("ازهر")) {
    return { text: "أزهري", type: "azhar" };
  }
  if (lowerName.includes("عام") || lowerName.includes("العام")) {
    return { text: "ثانوي عام", type: "general" };
  }
  if (lowerName.includes("معسكر") || lowerName.includes("انقاذ") || lowerName.includes("أنقاذ")) {
    return { text: "خصم مميز", type: "discount" };
  }
  if (lowerName.includes("مراجعة") || lowerName.includes("مراجعه") || lowerName.includes("نهائية") || lowerName.includes("نهائيه")) {
    return { text: "مراجعة نهائية", type: "revision" };
  }
  return null;
};

const getChapterImage = (name) => {
  if (!name) return FallbackImg;
  const lowerName = name.toLowerCase();
  if (lowerName.includes("معسكر النحو الشامل") && (lowerName.includes("عام") || lowerName.includes("العام"))) {
    return L1Img;
  }
  if (lowerName.includes("معسكر النحو الشامل") && (lowerName.includes("أزهر") || lowerName.includes("ازهر"))) {
    return L2Img;
  }
  if (lowerName.includes("أزهر") || lowerName.includes("ازهر")) {
    return ImgThird;
  }
  if (lowerName.includes("معسكر") || lowerName.includes("انقاذ") || lowerName.includes("أنقاذ")) {
    return ImgFirst;
  }
  if (lowerName.includes("مراجعة") || lowerName.includes("مراجعه") || lowerName.includes("نهائية") || lowerName.includes("نهائيه")) {
    return ImgSecond;
  }
  return FallbackImg;
};

const getChapterDescription = (name) => {
  return "شرح وتأسيس متكامل للمناهج الدراسية لطلاب الثانوية العامة والأزهرية، بأسلوب يجمع بين التبسيط والعمق الأكاديمي لضمان تحقيق الدرجة النهائية والتفوق الدراسي.";
};

export default function GradeCourses() {
  const { gradeId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);

  // Detect logged-in user's academic system to pre-select it
  const userStage = auth.user?.academic_year_id;
  const loggedInSystem = React.useMemo(() => {
    if (userStage === 10 || userStage === 14 || userStage === 11) return "azhar";
    if (userStage === 16 || userStage === 13 || userStage === 12) return "general";
    
    if (stages.length > 0) {
      const currentStage = stages.find(s => Number(s.id) === Number(userStage));
      if (currentStage) {
        const name = (currentStage.name || "").toLowerCase();
        if (name.includes("أزهر") || name.includes("ازهر")) return "azhar";
        return "general";
      }
    }
    return null;
  }, [userStage, stages]);

  const [system, setSystem] = useState("general");

  // Set system dynamically when user logs in, auth resolves, or query parameter is set
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const systemParam = queryParams.get("system");
    if (systemParam === "azhar" || systemParam === "general") {
      setSystem(systemParam);
    } else if (loggedInSystem) {
      setSystem(loggedInSystem);
    } else {
      setSystem("general");
    }
  }, [loggedInSystem]);

  // Helper to dynamically match academic year IDs by parsing stage names
  const findStageId = React.useCallback((allStages, gradeNum, sysType) => {
    const targetGrade = Number(gradeNum);
    const matched = allStages.find((stage) => {
      const name = (stage.name || "").toLowerCase();
      const isAzhar = name.includes("أزهر") || name.includes("ازهر");
      if (sysType === "azhar" && !isAzhar) return false;
      if (sysType === "general" && isAzhar) return false;

      let gradeOrder = 0;
      if (name.includes("أول") || name.includes("اول")) {
        gradeOrder = 1;
      } else if (
        name.includes("ثاني") ||
        name.includes("ثانى") ||
        name.includes("تانى") ||
        name.includes("ثانية") ||
        name.includes("تانية")
      ) {
        gradeOrder = 2;
      } else if (
        name.includes("ثالث") ||
        name.includes("تالت") ||
        name.includes("تالته") ||
        name.includes("تالتة")
      ) {
        gradeOrder = 3;
      }
      return gradeOrder === targetGrade;
    });
    return matched ? matched.id : null;
  }, []);

  const getSystemId = React.useCallback((gradeNum, sysType) => {
    const dynamicId = findStageId(stages, gradeNum, sysType);
    if (dynamicId !== null) return dynamicId;

    // Fallbacks matching common database environments
    if (Number(gradeNum) === 1) {
      return sysType === "general" ? 16 : 10;
    } else if (Number(gradeNum) === 2) {
      return sysType === "general" ? 13 : 14;
    } else {
      return sysType === "general" ? 12 : 11;
    }
  }, [stages, findStageId]);

  const targetAcademicIds = React.useMemo(() => {
    const generalId = getSystemId(gradeId, "general");
    const azharId = getSystemId(gradeId, "azhar");
    return [generalId, azharId];
  }, [gradeId, getSystemId]);

  const filteredCourses = React.useMemo(() => {
    if (!system) return [];
    const targetId = getSystemId(gradeId, system);
    return courses.filter(
      (course) => Number(course.academic_year_id || course.academicYearId) === targetId
    );
  }, [courses, system, gradeId, getSystemId]);

  // Grade Title adapts dynamically to the chosen system
  const currentStage = React.useMemo(() => {
    const targetId = getSystemId(gradeId, system);
    return stages.find(s => Number(s.id) === Number(targetId));
  }, [stages, gradeId, system, getSystemId]);

  const gradeTitle = currentStage ? `كورسات ${currentStage.name}` : `كورسات الصف ${Number(gradeId) === 1 ? "الأول" : Number(gradeId) === 2 ? "الثاني" : "الثالث"} الثانوي`;

  useEffect(() => {
    setLoading(true);
    axios.get(`${baseURL}/learning/academics`)
      .then(async (academicsRes) => {
        const allStages = academicsRes.data?.data || [];
        setStages(allStages);
        
        const generalId = findStageId(allStages, gradeId, "general") || (Number(gradeId) === 1 ? 16 : Number(gradeId) === 2 ? 13 : 12);
        const azharId = findStageId(allStages, gradeId, "azhar") || (Number(gradeId) === 1 ? 10 : Number(gradeId) === 2 ? 14 : 11);
        
        try {
          const [generalRes, azharRes] = await Promise.all([
            axios.get(`${baseURL}/learning/chapters/${generalId}`),
            axios.get(`${baseURL}/learning/chapters/${azharId}`)
          ]);

          const generalChapters = generalRes.data?.data || [];
          const azharChapters = azharRes.data?.data || [];
          const allChapters = [...generalChapters, ...azharChapters];

          let filtered = allChapters.filter((ch) => !ch.is_draft);

          const queryParams = new URLSearchParams(window.location.search);
          const teacherId = queryParams.get("teacher_id");
          if (teacherId && teacherId !== "all") {
            filtered = filtered.filter(
              (ch) => String(ch.teacher_id || "admin") === String(teacherId)
            );
          }

          const mapped = filtered.map((ch) => {
            const name = ch.name || "";
            let price = 150;
            let description = getChapterDescription(name);

            if (name.includes("معسكر النحو الشامل") && (name.includes("عام") || name.includes("العام"))) {
              price = 250;
              description = "معسكر النحو الشامل لطلاب الثانوية العامة - شرح كامل وتدريبات مكثفة على جميع قواعد النحو والاعراب.";
            } else if (name.includes("معسكر النحو الشامل") && (name.includes("أزهر") || name.includes("ازهر"))) {
              price = 250;
              description = "معسكر النحو الشامل لطلاب الثانوية الأزهرية - شرح كامل وتدريبات مكثفة متوافقة مع منهج الأزهر الشريف.";
            } else if (name.includes("الأول") || name.includes("اول")) {
              price = 125;
            } else if (name.includes("الثاني") || name.includes("تانى") || name.includes("ثانى")) {
              price = 150;
            } else if (name.includes("الثالث") || name.includes("تالت")) {
              price = 200;
            }

            const dates = getChapterDates(name);
            return {
              ...ch,
              price: (ch.price !== undefined && ch.price !== null) ? Number(ch.price) : price,
              image: resolveMediaUrl(ch.image_path) || ch.image || getChapterImage(name),
              description: ch.description || description,
              startDate: ch.startDate || dates.startDate,
              startTime: ch.startTime || dates.startTime,
            };
          });

          const sorted = mapped.sort((a, b) => {
            return (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id;
          });

          setCourses(sorted);
          setLoading(false);
        } catch (err) {
          console.error("Error loading chapters:", err);
          setCourses([]);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching courses for grade page:", err);
        setCourses([]);
        setLoading(false);
      });
  }, [gradeId, findStageId]);

  const handleCourseClick = (course) => {
    const stageId = course.academic_year_id || targetAcademicIds[0];
    if (!auth?.accessToken) {
      toast.info("يرجى إنشاء حساب أو تسجيل الدخول لعرض محتوى الكورس");
      navigate("/register", { state: { selectedStageId: stageId } });
      return;
    }

    if (auth.user?.is_staff) {
      navigate(`/learn/${stageId}?teacher_id=${course.teacher_id}`);
      return;
    }

    const enrolledId = auth.user?.academic_year_id;
    if (Number(stageId) !== Number(enrolledId)) {
      toast.error(
        "أنت لست مسجلاً في هذه المرحلة. يمكنك فقط الدخول إلى مرحلتك المسجلة."
      );
      return;
    }

    navigate(`/learn/${stageId}?teacher_id=${course.teacher_id}`);
  };

  const handleSubscribeClick = (course) => {
    const stageId = course.academic_year_id || targetAcademicIds[0];
    if (!auth?.accessToken) {
      toast.info("يرجى إنشاء حساب أو تسجيل الدخول لعرض الفاتورة والاشتراك");
      navigate("/register", { state: { selectedStageId: stageId } });
      return;
    }
    navigate(`/invoice/${course.id}`, {
      state: {
        price: Number(course.price ?? 150),
        discount: Number(course.discount ?? 0),
        courseName: course.name,
      },
    });
  };

  return (
    <div className="GradeCoursesPage" dir="rtl">
      <Header />
      <div className="grade-courses-header-section text-center py-5">
        <Container>
          <h1 className="grade-courses-main-title fw-bold">{gradeTitle}</h1>
          <p className="grade-courses-subtitle text-muted">
            {system 
              ? "اكتشف مجموعة متنوعة من الكورسات التعليمية المصممة خصيصاً لمساعدتك في تحقيق أهدافك الأكاديمية"
              : "اختر الشعبة الدراسية لعرض الكورسات التعليمية المتاحة لدفعة 2026"}
          </p>
        </Container>
      </div>

      {!system ? (
        <div className="system-selector-section py-5">
          <Container>
            <Row className="justify-content-center g-4">
              <Col xs={12} md={5} lg={4}>
                <div 
                  className="system-select-card"
                  onClick={() => setSystem("general")}
                >
                  <div className="system-select-icon-wrapper general-icon">
                    <span className="system-select-badge">عام</span>
                  </div>
                  <h3 className="system-select-title">ثانوي عام</h3>
                  <p className="system-select-desc">
                    الوصول لجميع كورسات وشرح منهج اللغة العربية لطلاب الثانوية العامة دفعة 2026 وفق النظام الدراسي الحديث.
                  </p>
                  <button className="system-select-btn btn-general">دخول القسم العام</button>
                </div>
              </Col>

              <Col xs={12} md={5} lg={4}>
                <div 
                  className="system-select-card"
                  onClick={() => setSystem("azhar")}
                >
                  <div className="system-select-icon-wrapper azhar-icon">
                    <span className="system-select-badge">أزهر</span>
                  </div>
                  <h3 className="system-select-title">ثانوية أزهرية</h3>
                  <p className="system-select-desc">
                    شرح وتدريبات متكاملة متوافقة مع المناهج المقررة لطلاب المعاهد الأزهرية (النحو، الصرف، البلاغة، الأدب).
                  </p>
                  <button className="system-select-btn btn-azhar">دخول قسم الأزهر</button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      ) : (
        <div className="grade-courses-grid-section py-5 bg-light">
          <Container>
            <div className="system-tabs-container mb-5 text-center d-flex justify-content-center gap-3">
              <button 
                className={`system-tab-btn ${system === "general" ? "active" : ""}`}
                onClick={() => setSystem("general")}
              >
                ثانوي عام (عام)
              </button>
              <button 
                className={`system-tab-btn ${system === "azhar" ? "active" : ""}`}
                onClick={() => setSystem("azhar")}
              >
                ثانوية أزهرية (أزهر)
              </button>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
                <p className="mt-3 text-muted">جاري تحميل الكورسات...</p>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <p>لا توجد كورسات متاحة حالياً لهذه السنة الدراسية في القسم المختار.</p>
              </div>
            ) : (
              <Row className="g-4 justify-content-center">
                {filteredCourses.map((course) => {
                  const badgeInfo = getCourseBadge(course.name);
                  return (
                    <Col key={course.id} xs={12} md={6} lg={4} className="d-flex">
                      <Card className="suggested-course-card stage-card w-100">
                        <div className="course-image-wrapper">
                          <img src={course.image} alt={course.name} />
                          {badgeInfo && (
                            <span className={`course-image-badge badge-${badgeInfo.type}`}>
                              {badgeInfo.text}
                            </span>
                          )}
                        </div>
                        <Card.Body className="d-flex flex-column text-end p-4">
                          <h3 className="course-card-title">{course.name}</h3>
                          {Number(course.discount) > 0 ? (
                            <div className="course-card-price text-end w-100 mb-2" style={{ direction: "rtl" }}>
                              <div className="text-primary-500 fw-bold text-end mb-1" style={{ color: "var(--brand-300)", fontSize: "1.35rem" }}>
                                {Number(course.price) - Number(course.discount)} جنيه
                              </div>
                              <div className="d-flex flex-row gap-1 justify-content-start align-items-center text-muted small" style={{ fontSize: "0.88rem" }}>
                                <span className="text-gray-600 dark:text-gray-200 smooth opacity-75">بدلاً من</span>
                                <span className="text-decoration-line-through text-danger fw-semibold" style={{ color: "#dc3545", fontWeight: "600" }}>{Number(course.price)} جنيهاً</span>
                              </div>
                            </div>
                          ) : (
                            <div className="course-card-price">{course.price} جنيه</div>
                          )}
                          
                          <div className="course-card-details-row">
                            <div className="course-card-desc-col">
                              <p className="course-card-desc">{course.description}</p>
                            </div>
                            <div className="course-meta-info-col">
                              <div className="course-meta-item">
                                <FaRegCalendarAlt className="meta-icon" />
                                <span>{course.startDate}</span>
                              </div>
                              <div className="course-meta-item">
                                <FaRegClock className="meta-icon" />
                                <span>{course.startTime || course.startDate}</span>
                              </div>
                            </div>
                          </div>

                          <div className="course-card-buttons mt-auto">
                            <button
                              onClick={() => handleCourseClick(course)}
                              className="btn-enter-course"
                            >
                              الدخول للكورس
                            </button>
                            <button
                              onClick={() => handleSubscribeClick(course)}
                              className="btn-subscribe-course"
                            >
                              الاشتراك في الكورس !
                            </button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Container>
        </div>
      )}
      <Footer />
    </div>
  );
}
